import { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import axios from "axios";
import { Iindicadores } from "../indicadores.interface";
import useCharts from "utils/customHooks/useCharts";
import { PieChart, Pie, Cell, Legend, Tooltip } from "recharts";

// Ajustamos la estructura de los datos en el frontend
interface MatriculadosNivel {
  nivel_estudio: string;
  total: number;
  diff_percent: string; // 🔹 Agregar esta propiedad
}

interface MatriculadosSexo {
  sexo: string;
  total: number;
  diff_percent: string; // 🔹 Agregar esta propiedad
}

// Ajustamos la respuesta completa de la API
interface MatriculadosStats {
  anio: number;
  total_matriculados: number;
  diff_percent_total: string;
  by_nivel: MatriculadosNivel[];
  by_sexo: MatriculadosSexo[];
}

// Interfaces para indicadores generales
interface Indicador {
  fecha_analisis: string;
  anio: number;
  total_docentes_en_utec: number;
  total_docentes_interior_en_utec: number;
  total_por_itr_sede: number;
  total_por_itr_sede_con_residencia_interior: number;
  itr: string;
  sede: string;
  fec_ini_periodo: string;
  fec_fin_periodo: string;
  porcentaje_de_radicacion: string;
}

interface Summary {
  anio: number;
  total_docentes: number;
  docentes_interior: number;
  porcentaje_interior: string;
  fecha_analisis?: string;
  cambios: {
    total_docentes: number;
    docentes_interior: number;
    porcentaje_interior: string;
  };
}

// Para "Personas Alcanzadas" (Cards)
// Datos devueltos por el endpoint /v1.1/indicadores/educacion
interface EducacionCardData {
  nivel_estudio: string;
  total: number;
  diff_percent: string;
}

interface EducacionCardResponse {
  anio: number;
  data: EducacionCardData[];
}

// Para "Personas Alcanzadas" (Tabla)
// Datos devueltos por el endpoint /v1.1/indicadores/educacion-form
interface EducacionTableData {
  itr: string;
  titulacion: string;
  nivel_estudio: string;
  total_personas_alcanzadas: number;
}
const COLORS = ["#3B00ED", "#9C27B0"]; // Azul para Hombres, Violeta para Mujeres

// Para "Estudiantes Matriculados"
interface EducacionEstu {
  fecha_analisis: string;
  anio: number;
  matriculados: number;
  sexo_de: string;
  nivel_estudio: string;
  itr: string;
  titulacion: string;
  fec_ini_periodo: string;
  fec_fin_periodo: string;
}

// Para "Nuevos Ingresos"
// Card: suma de ingresos y % de diferencia
// Para "Nuevos Ingresos" (Card con comparación anual)
interface IngresosNivel {
  nivel_estudio: string;
  total: number;
  diff_percent: string;
}

interface IngresosSexo {
  sexo: string;
  total: number;
  diff_percent: string;
}

// Actualizar la interfaz para incluir ingresos por nivel y sexo
interface IngresosCardData {
  total_ingresos: number;
  diff_percent_total: string;
  by_nivel: IngresosNivel[];
  by_sexo: IngresosSexo[];
}

// Tabla: detalle de ingresos
interface IngresosTableData {
  fecha_analisis: string;
  anio: number;
  ingresos: string; // O number, según corresponda
  sexo_de: string;
  nivel_estudio: string;
  itr: string;
  titulacion: string;
  fec_ini_periodo: string;
  fec_fin_periodo: string;
}

const IndicadoresTable = (props: Iindicadores) => {
  const { isLoadingCharts, charts } = useCharts(window.location.pathname, props.filters);

  // Estados generales
  const [data, setData] = useState<Indicador[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedITR, setSelectedITR] = useState<string | "todos">("todos");

  // Pestañas: 0: Presencia en territorio, 1: Educación, 2: (se usarán submenús en Educación)
  const [activeTab, setActiveTab] = useState<number>(0);
  // Submenú en Educación: 0: Personas Alcanzadas, 1: Estudiantes Matriculados, 2: Nuevos Ingresos
  const [subTabEdu, setSubTabEdu] = useState<number>(0);

  // Estados para "Personas Alcanzadas"
  const [educationCardData, setEducationCardData] = useState<EducacionCardResponse | null>(null);
  const [loadingEdu, setLoadingEdu] = useState<boolean>(false);
  const [errorEdu, setErrorEdu] = useState<string | null>(null);

  const [educationTableData, setEducationTableData] = useState<EducacionTableData[] | null>(null);
  const [loadingEduTable, setLoadingEduTable] = useState<boolean>(false);
  const [errorEduTable, setErrorEduTable] = useState<string | null>(null);

  // Estado para "Estudiantes Matriculados"
  const [educationEstuData, setEducationEstuData] = useState<EducacionEstu[] | null>(null);
  const [loadingEstu, setLoadingEstu] = useState<boolean>(false);
  const [errorEstu, setErrorEstu] = useState<string | null>(null);

  // Estados para "Nuevos Ingresos"
  const [ingresosCardData, setIngresosCardData] = useState<IngresosCardData | null>(null);
  const [loadingIngresosCard, setLoadingIngresosCard] = useState<boolean>(false);
  const [errorIngresosCard, setErrorIngresosCard] = useState<string | null>(null);

  const [ingresosTableData, setIngresosTableData] = useState<IngresosTableData[] | null>(null);
  const [loadingIngresosTable, setLoadingIngresosTable] = useState<boolean>(false);
  const [errorIngresosTable, setErrorIngresosTable] = useState<string | null>(null);

  // Filtros por ITR para secciones de Educación (para la tabla de Personas Alcanzadas y para Estudiantes)
  const [selectedITRForm, setSelectedITRForm] = useState<string>("todos");
  const [selectedITREstu, setSelectedITREstu] = useState<string>("todos");
  // También usamos el mismo filtro para la tabla de Nuevos Ingresos
  const [selectedITRIngresos, setSelectedITRIngresos] = useState<string>("todos");

  const yearFilter = Array.isArray(props.filters)
    ? props.filters.find((filter: any) => filter.codigo === "year")
    : null;
  const selectedYear = yearFilter?.valor || 2024;
  
// 📌 Estado para almacenar los datos de matriculados
const [matriculadosData, setMatriculadosData] = useState<MatriculadosStats | null>(null);
const [loadingMatriculados, setLoadingMatriculados] = useState<boolean>(false);
const [errorMatriculados, setErrorMatriculados] = useState<string | null>(null);



// 📌 Cargar datos de matriculación desde el backend
useEffect(() => {
  if (activeTab === 1 && subTabEdu === 1) {
    setLoadingMatriculados(true);
    axios
      .post<MatriculadosStats>("https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/indicadores/matriculados-stats", {
        year: selectedYear,
      })
      .then((response) => {
        setMatriculadosData(response.data);
        setErrorMatriculados(null);
      })
      .catch((error) => {
        console.error("Error fetching matriculados data:", error);
        setErrorMatriculados("Error al obtener datos de matriculación.");
      })
      .finally(() => setLoadingMatriculados(false));
  }
}, [selectedYear, activeTab, subTabEdu]);
  // Indicadores institucionales (Presencia en territorio)
  useEffect(() => {
    setLoading(true);
    axios
      .post<{ data: Indicador[] }>("https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/indicadores", {
        year: selectedYear,
        itr: selectedITR !== "todos" ? selectedITR : undefined,
      })
      .then((response) => {
        const indicadores = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setData(indicadores);
      })
      .catch((error) => {
        let errorMessage = "Error al obtener los datos.";
        if (error.response?.data) {
          errorMessage = error.response.data.message || JSON.stringify(error.response.data);
        } else if (error.message) {
          errorMessage = error.message;
        }
        setError(errorMessage);
      })
      .finally(() => setLoading(false));
  }, [selectedYear, selectedITR]);

  const availableITRs = ["ITRSO", "ITRCS", "ITRE", "ITRN", "ULO"];

  // Resumen
  useEffect(() => {
    axios
      .get<Summary>(`https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/indicadores/resumen/${selectedYear}`)
      .then((response) => {
        setSummary(response.data);
      })
      .catch(() => {
        setSummary(null);
      });
  }, [selectedYear]);

  // Datos para "Personas Alcanzadas" (Cards)
  useEffect(() => {
    if (activeTab === 1 && subTabEdu === 0) {
      setLoadingEdu(true);
      axios
        .post<EducacionCardResponse>("https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/indicadores/educacion", { year: selectedYear })
        .then((response) => {
          console.log("Respuesta de educacion (cards):", response.data);
          setEducationCardData(response.data);
          setErrorEdu(null);
        })
        .catch((error) => {
          console.error("Error fetching education cards data:", error);
          setErrorEdu("Error al obtener los datos de educación.");
        })
        .finally(() => setLoadingEdu(false));
    }
  }, [selectedYear, activeTab, subTabEdu]);

  // Datos para "Personas Alcanzadas" (Tabla)
  useEffect(() => {
    if (activeTab === 1 && subTabEdu === 0) {
      setLoadingEduTable(true);
      axios
        .post<EducacionTableData[]>("https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/indicadores/educacion-form", { year: selectedYear })
        .then((response) => {
          console.log("Respuesta de educacion-form (tabla):", response.data);
          setEducationTableData(response.data);
        })
        .catch((error) => {
          console.error("Error fetching education table data:", error);
          setErrorEduTable("Error al obtener los datos de la tabla de educación.");
        })
        .finally(() => setLoadingEduTable(false));
    }
  }, [selectedYear, activeTab, subTabEdu]);

  // Datos para "Estudiantes Matriculados"
  useEffect(() => {
    if (activeTab === 1 && subTabEdu === 1) {
      setLoadingEstu(true);
      axios
        .post<EducacionEstu[]>("https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/indicadores/educacion-estu", { year: selectedYear })
        .then((response) => {
          setEducationEstuData(response.data);
          setErrorEstu(null);
        })
        .catch((error) => {
          console.error("Error fetching education estudiantes data:", error);
          setErrorEstu("Error al obtener datos de educación de estudiantes.");
        })
        .finally(() => setLoadingEstu(false));
    }
  }, [selectedYear, activeTab, subTabEdu]);

  // Datos para "Nuevos Ingresos" (Card)
  useEffect(() => {
    if (activeTab === 1 && subTabEdu === 2) {
      setLoadingIngresosCard(true);
      axios
        .post<IngresosCardData>("https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/indicadores/educacion-ingresos", { year: selectedYear })
        .then((response) => {
          console.log("✅ Datos recibidos en ingresosCardData:", response.data);
          setIngresosCardData(response.data);
          setErrorIngresosCard(null);
        })
        .catch((error) => {
          console.error("🚨 Error fetching ingresos card data:", error);
          setErrorIngresosCard("Error al obtener los datos de ingresos.");
        })
        .finally(() => setLoadingIngresosCard(false));
    }
  }, [selectedYear, activeTab, subTabEdu]);
  
  

  // Datos para "Nuevos Ingresos" (Tabla)
  useEffect(() => {
    if (activeTab === 1 && subTabEdu === 2) {
      setLoadingIngresosTable(true);
      axios
        .post<IngresosTableData[]>("https://ec2-18-231-158-5.sa-east-1.compute.amazonaws.com:4000/v1.1/indicadores/educacion-ingresos-form", { year: selectedYear })
        .then((response) => {
          console.log("Respuesta de ingresos (tabla):", response.data);
          setIngresosTableData(response.data);
          setErrorIngresosTable(null);
        })
        .catch((error) => {
          console.error("Error fetching ingresos table data:", error);
          setErrorIngresosTable("Error al obtener los datos de la tabla de ingresos.");
        })
        .finally(() => setLoadingIngresosTable(false));
    }
  }, [selectedYear, activeTab, subTabEdu]);

  // Filtrado para "Personas Alcanzadas" (Cards)
  const filteredCardData = educationCardData?.data ?? [];
  // Filtrado para "Personas Alcanzadas" (Tabla)
  const filteredTableData = educationTableData ?? [];
  // Filtrado para "Estudiantes Matriculados" (Tabla)
  const filteredEstuData = educationEstuData
    ? educationEstuData.filter(
        (item) => selectedITREstu === "todos" || item.itr === selectedITREstu
      )
    : [];
  // Filtrado para "Nuevos Ingresos" (Tabla)
  const filteredIngresosData = ingresosTableData ?? [];
  // Indicadores de "Presencia en territorio"
  const filteredData = data.filter(
    (item) => selectedITR === "todos" || item.itr === selectedITR
  );

  return (
    <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
      <Grid item xs={12}>
        <Typography variant="h4" fontWeight="bold">
          Indicadores Institucionales
        </Typography>
      </Grid>

      {/* Pestañas principales */}
      <Grid item xs={12} sx={{ borderBottom: "2px solidrgb(211, 171, 171)", pb: 1 }}>
  <Tabs
    value={activeTab}
    onChange={(_, newValue) => setActiveTab(newValue)}
    sx={{
      "& .MuiTabs-flexContainer": {
        borderBottom: "2px solid #E0E0E0", // Línea gris que se extiende en todas las pestañas
      },
      "& .MuiTabs-indicator": {
        backgroundColor: "#1976d2", // Línea azul para la pestaña activa
        height: "2px", // Ajuste para que se solape con la línea gris
      },
      "& .MuiTab-root": {
        textTransform: "none",
        fontSize: "16px",
        fontWeight: "bold",
      },
    }}
  >
    <Tab label="Presencia en territorio" />
    <Tab label="Educación" />
    <Tab label="Investigación, vinculación con el medio e innovación" />
  </Tabs>
</Grid>


      {activeTab === 0 && (
        <>
          {/* Contenido de Presencia en territorio */}
          {/* ... Aquí va el contenido existente para esta pestaña ... */}
        </>
      )}

      {activeTab === 1 && (
        <>
          {/* Submenú en Educación */}
          <Grid item xs={12}>
          <Grid item xs={6} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="educacion-select-label">Seleccione opción</InputLabel>
              <Select
                labelId="educacion-select-label"
                value={subTabEdu}
                label="Seleccione opción"
                onChange={(e) => setSubTabEdu(Number(e.target.value))}
              >
                <MenuItem value={0}>Personas Alcanzadas</MenuItem>
                <MenuItem value={1}>Estudiantes Matriculados</MenuItem>
                <MenuItem value={2}>Nuevos Ingresos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          </Grid>
          {/* Sección de Personas Alcanzadas */}
          <Typography variant="h4" sx={{ mt: 4, mb: 4 }}>
  Datos Generales
</Typography>

{subTabEdu === 0 && (
  <>
    {loadingEdu || loadingEduTable ? (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh", // Ocupar toda la pantalla
        }}
      >
        <CircularProgress />
      </Box>
    ) : errorEdu || errorEduTable ? (
      <Typography color="error">{errorEdu || errorEduTable}</Typography>
    ) : educationCardData && educationTableData ? (
      <>
{/* Cards */}
<Grid container spacing={2} sx={{ mb: 2 }}>
  {filteredCardData.map((item: EducacionCardData, index: number) => {
    // Verificar si es la primera card (Total General)
    const isTotalGeneral = item.nivel_estudio === "Total";

    // Asignar el color de fondo según el tipo de card
    const cardBgColor = isTotalGeneral ? "#CCEAF3" : "#E6F5F9";

    // Formatear el número de diferencia (diff_percent)
    const diffValue = parseFloat(item.diff_percent.replace("%", "")) || 0;
    const formattedDiff = `(${diffValue >= 0 ? "+" : ""}${diffValue} % año anterior)`;

    return (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <Card sx={{ backgroundColor: cardBgColor, height: "100%" }}>
          <CardContent>
            <Typography variant="h6">{item.nivel_estudio}</Typography>
            <Typography variant="h4" fontWeight="bold">{item.total}</Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {formattedDiff}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  })}
</Grid>


        {/* Filtro para la tabla */}
  

        <Grid item xs={12}>
  <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
    Distribución de Personas Alcanzadas en el territorio
  </Typography>

  <Grid container spacing={0} sx={{ alignItems: "center", display: "flex" }}>
    <Typography variant="subtitle1" sx={{ mr: 2 }}>
      Filtrar por
    </Typography>
    <FormControl variant="outlined" size="small">
      <InputLabel>ITR</InputLabel>
      <Select
        value={selectedITRForm}
        onChange={(e) => setSelectedITRForm(e.target.value as string)}
        label="ITR"
      >
        <MenuItem value="todos">Todos</MenuItem>
        <MenuItem value="ITRSO">ITRSO</MenuItem>
        <MenuItem value="ITRCS">ITRCS</MenuItem>
        <MenuItem value="ITRN">ITRN</MenuItem>
        <MenuItem value="ITRE">ITRE</MenuItem>
        <MenuItem value="ULO">ULO</MenuItem>
      </Select>
    </FormControl>
  </Grid>
</Grid>



        <Grid item xs={12}>
    

        <Grid container spacing={2} sx={{ mt: 2 }}>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Fecha de Análisis
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {new Date().toLocaleDateString("es-ES")}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Año
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {selectedYear}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Período
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          21/12/{selectedYear}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>


        </Grid>

        {/* Tabla de Personas Alcanzadas */}
        <Grid item xs={12}>
  <Card sx={{ p: 2 }}>
    <CardContent>
      <Typography variant="h6" fontWeight="bold">
        Personas alcanzadas por Sede
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Cantidad de personas alcanzadas por sede y con residencia en el interior.
      </Typography>
    </CardContent>

    {/* 🔹 Scroll en la tabla si hay muchas filas */}
    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: "800px", overflowY: "auto" }}>
      <Table stickyHeader> {/* 🔹 Mantiene la cabecera fija al hacer scroll */}
        <TableHead sx={{ backgroundColor: "#F5F5F5" }}>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>ITR</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Titulación</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Nivel Estudio</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Total Personas Alcanzadas</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredTableData
            .filter((item) => selectedITRForm === "todos" || item.itr === selectedITRForm)
            .map((item: EducacionTableData, index: number) => (
              <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? "white" : "#F5F5F5" }}>
                <TableCell>{item.itr}</TableCell>
                <TableCell>{item.titulacion}</TableCell>
                <TableCell>{item.nivel_estudio}</TableCell>
                <TableCell>{item.total_personas_alcanzadas}</TableCell>
              </TableRow>
            ))}

          {/* 🔹 Línea separadora antes de los totales */}
          <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
            <TableCell colSpan={4} sx={{ height: "8px", padding: 0 }}></TableCell>
          </TableRow>

          {/* 🔹 Fila de totales */}
          <TableRow sx={{ backgroundColor: "#F5F5F5", fontWeight: "bold" }}>
            <TableCell colSpan={3} sx={{ textAlign: "right", fontWeight: "bold" }}>
              TOTALES
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {
                filteredTableData
                  .filter((item) => selectedITRForm === "todos" || item.itr === selectedITRForm)
                  .reduce((sum, row) => sum + Number(row.total_personas_alcanzadas), 0)
              }
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
</Grid>


      </>
    ) : (
      <Typography variant="body1">No hay datos de educación.</Typography>
    )}
  </>
)}


          {/* Sección de Estudiantes Matriculados */}
          {subTabEdu === 1 && (
            <>
              <Grid item xs={12} sx={{ mb: 1 }}>

               {/* 📌 Sección de Cards antes de la tabla */}
               
    <Grid container spacing={2} sx={{ mt: 2, justifyContent: "center" }}>
      {loadingMatriculados ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "20vh" }}>
          <CircularProgress />
        </Box>
      ) : errorMatriculados ? (
        <Typography color="error">{errorMatriculados}</Typography>
      ) : matriculadosData ? (
        <>
<Grid>

</Grid>
<Grid container spacing={2} sx={{ mt: 2, justifyContent: "flex-start", alignItems: "start" }}>
  {/* Total Matriculados */}
  <Grid item xs={12} sm={6} md={4}>
    <Card sx={{ backgroundColor: "#CCEAF3" }}>
      <CardContent>
        <Typography variant="h6">Total Matriculados</Typography>
        <Typography variant="h4" fontWeight="bold">{matriculadosData.total_matriculados}</Typography>
        <Typography variant="subtitle1">
          ({matriculadosData.diff_percent_total} año anterior)
        </Typography>
      </CardContent>
    </Card>
  </Grid>

  {/* Matriculados por Nivel de Estudio */}
  {matriculadosData.by_nivel.map((nivel, index) => (
    <Grid item xs={12} sm={6} md={4} key={index}>
      <Card sx={{ backgroundColor: "#E6F5F9" }}>
        <CardContent>
          <Typography variant="h6">{nivel.nivel_estudio}</Typography>
          <Typography variant="h4" fontWeight="bold">{nivel.total}</Typography>
          <Typography variant="subtitle1">
            ({nivel.diff_percent} año anterior)
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  ))}

  {/* Matriculados por Sexo */}



  <Grid item xs={12}>
  <Card sx={{ p: 2 }}>
    <CardContent>
      {/* 🔍 Console log para depuración */}
      {console.log("📊 Datos para el gráfico:", matriculadosData?.by_sexo)}

      <Typography variant="h6" fontWeight="bold">
        Género
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Distribución del total de estudiantes matriculados por Género
      </Typography>

      {/* 📌 Renderizar el PieChart */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        {matriculadosData?.by_sexo && matriculadosData.by_sexo.length > 0 ? (
          <PieChart width={500} height={300}>
            <Pie
              data={matriculadosData.by_sexo.map(sexo => ({
                name: sexo.sexo,
                value: Number(sexo.total)
              }))}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={3}
              dataKey="value"
            >
              {matriculadosData.by_sexo.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        ) : (
          <Typography variant="body2" sx={{ textAlign: "center", color: "gray" }}>
            No hay datos disponibles
          </Typography>
        )}
      </Box>

      {/* 📌 Variación porcentual respecto al año anterior */}
      <Box
        sx={{
          backgroundColor: "#F0F4FF",
          p: 1,
          borderRadius: "8px",
          mt: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="body2">
          {matriculadosData.by_sexo
            .map((sexo) => `${sexo.diff_percent} de ${sexo.sexo} que año anterior`)
            .join(" y ")}
        </Typography>
      </Box>
    </CardContent>
  </Card>
</Grid>


</Grid>


    
        </>
      ) : (
        <Typography variant="body1">No hay datos de matriculación.</Typography>
      )}
    </Grid>

{/* 📌 Título con más espacio arriba */}
<Grid sx={{ mt: 6, mb: 2 }}>
  <Typography variant="h4">
  Distribución de Estudiantes matriculados en el territorio
  </Typography>
</Grid>


</Grid>
              
                
    {/* 📌 Filtro de ITR */}
  <Grid container spacing={0} sx={{ alignItems: "center", display: "flex", mb: 2 }}>
  <Typography variant="subtitle1" sx={{ mr: 2 }}>
    Filtrar por
  </Typography>
  <FormControl variant="outlined" size="small">
    <InputLabel>ITR</InputLabel>
    <Select
      value={selectedITREstu}
      onChange={(e) => setSelectedITREstu(e.target.value as string)}
      label="ITR"
    >
      <MenuItem value="todos">Todos</MenuItem>
      <MenuItem value="ITRCS">ITRCS</MenuItem>
      <MenuItem value="ITRSO">ITRSO</MenuItem>
      <MenuItem value="ITRN">ITRN</MenuItem>
      <MenuItem value="ITRE">ITRE</MenuItem>
      <MenuItem value="ULO">ULO</MenuItem>
    </Select>
  </FormControl>
</Grid>


              <Grid container spacing={2} sx={{ mt: 2, justifyContent: "center" }}>
  
  
  
              <Grid container spacing={2} sx={{ mt: 2 }}>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Fecha de Análisis
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {new Date().toLocaleDateString("es-ES")}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Año
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {selectedYear}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Período
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          21/12/{selectedYear}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>
</Grid>

              {loadingEstu ? (
                <CircularProgress />
              ) : errorEstu ? (
                <Typography color="error">{errorEstu}</Typography>
              ) : educationEstuData ? (
                <>
<Grid item xs={12}>
  <Card sx={{ p: 2 }}>
    <CardContent>
      <Typography variant="h6" fontWeight="bold">
        Estudiantes matriculados por Sede
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Estudiantes según la oferta educativa a la que están matriculados hasta el período actual.
      </Typography>
    </CardContent>

    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: "400px", overflowY: "auto"  }}>
      <Table stickyHeader>
        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>ITR</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Titulación</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Nivel Estudio</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Matriculados</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Sexo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {educationEstuData
            .filter((item) => selectedITREstu === "todos" || item.itr === selectedITREstu)
            .map((row: EducacionEstu, index: number) => (
              <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? "white" : "#f5f5f5" }}>
                <TableCell>{row.itr}</TableCell>
                <TableCell>{row.titulacion}</TableCell>
                <TableCell>{row.nivel_estudio}</TableCell>
                <TableCell>{row.matriculados}</TableCell>
                <TableCell>{row.sexo_de}</TableCell>
              </TableRow>
            ))}

          {/* 🔹 Línea separadora antes de los totales */}
          <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
            <TableCell colSpan={5} sx={{ height: "8px", padding: 0 }}></TableCell>
          </TableRow>

          {/* 🔹 Fila de totales */}
          <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
  <TableCell colSpan={3} sx={{ textAlign: "right", fontWeight: "bold" }}>
    TOTALES
  </TableCell>
  <TableCell sx={{ fontWeight: "bold" }}>
    {educationEstuData?.length > 0
      ? educationEstuData
          .filter((item) => selectedITREstu === "todos" || item.itr === selectedITREstu)
          .reduce((sum, row) => sum + (Number(row.matriculados) || 0), 0)
      : 0}
  </TableCell>
  <TableCell></TableCell> {/* Celda vacía para mantener la estructura */}
</TableRow>

        </TableBody>
      </Table>
    </TableContainer>
  </Card>
</Grid>

                </>
              ) : (
                <Typography variant="body1">No hay datos de estudiantes.</Typography>
              )}
            </>
          )}

          {/* Sección de Nuevos Ingresos */}
          {subTabEdu === 2 && (
  <>


    {loadingIngresosCard ? (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "20vh",
    }}
  >
    <CircularProgress />
  </Box>
) : errorIngresosCard ? (
  <Typography color="error">{errorIngresosCard}</Typography>
) : ingresosCardData ? (
  <Grid container spacing={2} sx={{ mt: 2, justifyContent: "flex-start", alignItems: "start" }}>

  {/* 📌 Tarjeta de Total Ingresos */}
  <Grid item xs={12} sm={6} md={4}>
    <Card sx={{ backgroundColor: "#CCEAF3" }}>
      <CardContent>
        <Typography variant="h6">Total Ingresos</Typography>
        <Typography variant="h4" fontWeight="bold">{ingresosCardData.total_ingresos}</Typography>
        <Typography variant="subtitle1">
          ({ingresosCardData.diff_percent_total} año anterior)
        </Typography>
      </CardContent>
    </Card>
  </Grid>

  {/* 📌 Tarjetas de Ingresos por Nivel de Estudio */}
  {ingresosCardData.by_nivel.map((nivel, index) => (
    <Grid item xs={12} sm={6} md={4} key={index} sx={{ alignSelf: "start" }}>
      <Card sx={{ backgroundColor: "#E6F5F9" }}>
        <CardContent>
          <Typography variant="h6">{nivel.nivel_estudio}</Typography>
          <Typography variant="h4" fontWeight="bold">{nivel.total}</Typography>
          <Typography variant="subtitle1">
            ({nivel.diff_percent} año anterior)
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  ))}

  {/* 📌 Tarjetas de Ingresos por Sexo */}
  <Grid item xs={12}>
  <Card sx={{ p: 2 }}>
    <CardContent>
      <Typography variant="h6" fontWeight="bold">
        Género
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Distribución del total de nuevos ingresos por Género
      </Typography>

      {/* 📌 Renderizar el PieChart */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <PieChart width={500} height={300}>
          <Pie
            data={ingresosCardData.by_sexo.map(sexo => ({
              name: sexo.sexo,
              value: Number(sexo.total), // Convertimos total a número
            }))}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={120}
            paddingAngle={3}
            dataKey="value"
          >
            {ingresosCardData.by_sexo.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </Box>

      {/* 📌 Variación porcentual respecto al año anterior */}
      <Box
        sx={{
          backgroundColor: "#F0F4FF",
          p: 1,
          borderRadius: "8px",
          mt: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="body2">
          {ingresosCardData.by_sexo
            .map((sexo) => `${sexo.diff_percent} de ${sexo.sexo} que año anterior`)
            .join(" y ")}
        </Typography>
      </Box>
    </CardContent>
  </Card>
</Grid>

</Grid>

) : (
  <Typography variant="body1">No hay datos de ingresos.</Typography>
)}
{/* 📌 Título con más espacio arriba */}
<Grid sx={{ mt: 6, mb: 2 }}>
  <Typography variant="h4">
    Distribución de Ingresos en el territorio
  </Typography>
</Grid>

    {/* Filtro para la tabla de Nuevos Ingresos */}
<Grid container spacing={0} sx={{ alignItems: "center", display: "flex", mb: 2 }}>
  <Typography variant="subtitle1" sx={{ mr: 2 }}>
    Filtrar por
  </Typography>
  <FormControl variant="outlined" size="small">
    <InputLabel>ITR</InputLabel>
    <Select
      value={selectedITRIngresos}
      onChange={(e) => setSelectedITRIngresos(e.target.value as string)}
      label="ITR"
    >
      <MenuItem value="todos">Todos</MenuItem>
      <MenuItem value="ITRCS">ITRCS</MenuItem>
      <MenuItem value="ITRSO">ITRSO</MenuItem>
      <MenuItem value="ITRN">ITRN</MenuItem>
      <MenuItem value="ITRE">ITRE</MenuItem>
      <MenuItem value="ULO">ULO</MenuItem>
    </Select>
  </FormControl>
</Grid>



    
<Grid container spacing={2} sx={{ mt: 2 }}>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Fecha de Análisis
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {new Date().toLocaleDateString("es-ES")}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Año
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {selectedYear}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Período
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          21/12/{selectedYear}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>



    {loadingIngresosTable ? (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "20vh",
        }}
      >
        <CircularProgress />
      </Box>
    ) : errorIngresosTable ? (
      <Typography color="error">{errorIngresosTable}</Typography>
    ) : ingresosTableData ? (
<Grid item xs={12}>
{/* 🔹 Tabla de Ingresos por Sede con el filtro aplicado */}
<Grid item xs={12}>
  <Card sx={{ p: 2 }}>
    <CardContent>
      <Typography variant="h6" fontWeight="bold">
        Ingresos por Sede
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Estudiantes ingresados según la oferta educativa a la que se han registrado hasta el período actual.
      </Typography>
    </CardContent>

    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
      <Table stickyHeader>
        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>ITR</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Titulación</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Nivel Estudio</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Ingresos</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Sexo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ingresosTableData
            .filter((item) => selectedITRIngresos === "todos" || item.itr === selectedITRIngresos)
            .map((row: IngresosTableData, index: number) => (
              <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? "white" : "#f5f5f5" }}>
                <TableCell>{row.itr}</TableCell>
                <TableCell>{row.titulacion}</TableCell>
                <TableCell>{row.nivel_estudio}</TableCell>
                <TableCell>{row.ingresos}</TableCell>
                <TableCell>{row.sexo_de}</TableCell>
              </TableRow>
            ))}

          {/* 🔹 Línea separadora antes de los totales */}
          <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
            <TableCell colSpan={5} sx={{ height: "8px", padding: 0 }}></TableCell>
          </TableRow>

          {/* 🔹 Fila de totales */}
          <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
            <TableCell colSpan={3} sx={{ textAlign: "right", fontWeight: "bold" }}>
              TOTALES
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {ingresosTableData
                .filter((item) => selectedITRIngresos === "todos" || item.itr === selectedITRIngresos)
                .reduce((sum, row) => sum + (Number(row.ingresos) || 0), 0)}
            </TableCell>
            <TableCell></TableCell> {/* Celda vacía para mantener la estructura */}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
</Grid>
</Grid>

    ) : (
      <Typography variant="body1">No hay datos de ingresos.</Typography>
    )}
  </>
)}

        </>
      )}

      {activeTab === 2 && (
        <>
          {/* Contenido de la pestaña Investigación, vinculación con el medio e innovación */}
          {/* ... Aquí va el contenido existente para esta pestaña ... */}
        </>
      )}

      {activeTab === 0 && (
        <>
                  <Typography variant="h4" sx={{ mt: 4, mb: 4 }}>
  Datos Generales
</Typography>

{/* Tarjetas de resumen y tabla en la pestaña Presencia en territorio */}
{summary && (
  <Grid container spacing={2} sx={{ mb: 2 }}>
    {/* Primera Card - Total Docentes */}
    <Grid item xs={4}>
      <Card sx={{ backgroundColor: "#CCEAF3" }}>
        <CardContent>
          <Typography variant="h6">Total Docentes</Typography>
          <Typography variant="h4" fontWeight="bold">
            {summary?.total_docentes ?? "N/A"}
          </Typography>
          <Typography color="textSecondary">
            (+{summary?.cambios?.total_docentes ?? 0} año anterior)
          </Typography>
        </CardContent>
      </Card>
    </Grid>

    {/* Segunda Card - Docentes en el Interior */}
    <Grid item xs={4}>
      <Card sx={{ backgroundColor: "#E6F5F9" }}>
        <CardContent>
          <Typography variant="h6">Docentes en el Interior</Typography>
          <Typography variant="h4" fontWeight="bold">
            {summary?.docentes_interior ?? "N/A"}
          </Typography>
          <Typography color="textSecondary">
            (+{summary?.cambios?.docentes_interior ?? 0} año anterior)
          </Typography>
        </CardContent>
      </Card>
    </Grid>

    {/* Tercera Card - % Docentes en Interior */}
    <Grid item xs={4}>
      <Card sx={{ backgroundColor: "#E6F5F9" }}>
        <CardContent>
          <Typography variant="h6">% Docentes en Interior</Typography>
          <Typography variant="h4" fontWeight="bold">
            {summary?.porcentaje_interior ?? "N/A"}
          </Typography>
          <Typography color="textSecondary">
            (+{summary?.cambios?.porcentaje_interior ?? "0"}% año anterior)
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
)}


<Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
  Distribución de Docentes en el territorio
</Typography>


<Grid container spacing={0} sx={{ alignItems: "center", display: "flex" }}>
  <Typography variant="subtitle1"  sx={{ mr: 2 }}>
    Filtrar por
  </Typography>
  <FormControl variant="outlined" size="small">
    <InputLabel>ITR</InputLabel>
    <Select
      value={selectedITR}
      onChange={(e) => setSelectedITR(e.target.value as string | "todos")}
      label="ITR"
    >
      <MenuItem value="todos">Todos</MenuItem>
      {availableITRs.map((itr) => (
        <MenuItem key={itr} value={itr}>
          {itr}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>




<Grid container spacing={2} sx={{ mt: 2 }}>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Fecha de Análisis
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {new Date().toLocaleDateString("es-ES")}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Año
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {selectedYear}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Card sx={{ backgroundColor: "#FFFFFF", textAlign: "left", width: "100%", border: "1px solid #DFE0EB" }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary">
          Período
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          21/12/{selectedYear}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
</Grid>



<Grid item xs={12}>
  <Card sx={{ p: 2 }}>
    <CardContent>
      <Typography variant="h6" fontWeight="bold">
        Docentes por Sede
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Cantidad de docentes por sede y con residencia en el interior.
      </Typography>
    </CardContent>
    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: "800px", overflowY: "auto"  }}>
      <Table>
        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>ITR</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Sede</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Total asignado a la sede</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Total con residencia en interior</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.map((row: Indicador, index: number) => (
            <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? "white" : "#f5f5f5" }}>
              <TableCell>{row.itr}</TableCell>
              <TableCell>{row.sede}</TableCell>
              <TableCell>{row.total_por_itr_sede}</TableCell>
              <TableCell>{row.total_por_itr_sede_con_residencia_interior}</TableCell>
            </TableRow>
          ))}

          {/* 🔹 Línea separadora antes de los totales */}
          <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
            <TableCell colSpan={4} sx={{ height: "8px", padding: 0 }}></TableCell>
          </TableRow>

          {/* 🔹 Fila de totales */}
          <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
            <TableCell colSpan={2} sx={{ textAlign: "right", fontWeight: "bold" }}>
              TOTALES
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {filteredData.reduce((sum, row) => sum + Number(row.total_por_itr_sede), 0)}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {filteredData.reduce((sum, row) => sum + Number(row.total_por_itr_sede_con_residencia_interior), 0)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
</Grid>


        </>
      )}
    </Grid>
  );
};

const mapStateToProps = (state: any) => ({
  filters: state.filters || [],
});

export default connect(mapStateToProps)(IndicadoresTable);



